import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { preventiviApi } from '../api/client';

const PreventiviList = () => {
  const [preventivi, setPreventivi] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadPreventivi();
  }, []);

  const loadPreventivi = async () => {
    try {
      setLoading(true);
      const response = await preventiviApi.getAll();
      setPreventivi(response.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, nome) => {
    if (!window.confirm(`Sei sicuro di voler eliminare il preventivo per "${nome}"?`)) return;
    try {
      await preventiviApi.delete(id);
      loadPreventivi();
    } catch (err) {
      console.error(err);
      alert('Errore durante l\'eliminazione');
    }
  };

  const filteredPreventivi = preventivi.filter(prev =>
    (prev.cliente_nome && prev.cliente_nome.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (prev.oggetto && prev.oggetto.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (prev.numero_preventivo && prev.numero_preventivo.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const getStatoStyle = (stato) => {
    switch (stato) {
      case 'approvato':
        return 'bg-green-50 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800';
      case 'inviato':
        return 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800';
      case 'rifiutato':
        return 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700';
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch {
      return '-';
    }
  };

  if (loading) return <div className="flex justify-center h-screen items-center"><div className="spinner"></div></div>;

  return (
    <div className="animate-fade-in">
      <div className="flex justify-between items-end mb-12">
        <div>
          <h1 className="text-4xl font-bold mb-2 tracking-tight text-black dark:text-white">Preventivi</h1>
          <p className="text-gray-500 dark:text-gray-400">{preventivi.length} preventiv{preventivi.length === 1 ? 'o' : 'i'} totali</p>
        </div>
        <div className="flex items-center gap-4">
          {/* Search */}
          <div className="relative">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Cerca..."
              className="pl-8 pr-4 py-2 text-sm bg-white dark:bg-[#1a1a1a] border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:border-black dark:focus:border-white transition-colors w-48"
            />
            <svg className="w-4 h-4 text-gray-400 absolute left-2.5 top-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <Link
            to="/preventivi/nuovo"
            className="bg-black hover:bg-gray-800 text-white dark:bg-white dark:text-black dark:hover:bg-gray-200 px-6 py-3 rounded-lg font-medium text-sm transition-colors flex items-center gap-2"
          >
            <span>+</span> Nuovo Preventivo
          </Link>
        </div>
      </div>

      <div className="bg-white dark:bg-[#1a1a1a] rounded-xl border border-gray-100 dark:border-gray-800 overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b border-gray-100 dark:border-gray-800">
                <th className="py-4 px-6 text-xs font-medium text-gray-400 uppercase tracking-wider">Numero</th>
                <th className="py-4 px-6 text-xs font-medium text-gray-400 uppercase tracking-wider">Cliente</th>
                <th className="py-4 px-6 text-xs font-medium text-gray-400 uppercase tracking-wider">Oggetto</th>
                <th className="py-4 px-6 text-xs font-medium text-gray-400 uppercase tracking-wider">Stato</th>
                <th className="py-4 px-6 text-xs font-medium text-gray-400 uppercase tracking-wider">Data</th>
                <th className="py-4 px-6 text-xs font-medium text-gray-400 uppercase tracking-wider text-right">Totale</th>
                <th className="py-4 px-6 text-xs font-medium text-gray-400 uppercase tracking-wider text-right">Azioni</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50 dark:divide-gray-800">
              {filteredPreventivi.map((prev) => {
                const total = prev.totale_scontato || prev.totale || 0;

                return (
                  <tr key={prev.id} className="hover:bg-gray-50 dark:hover:bg-gray-900 transition-colors group">
                    <td className="py-4 px-6 text-sm font-mono text-gray-600 dark:text-gray-400">
                      <div className="flex items-center gap-2">
                        <span>{prev.numero_preventivo}</span>
                        {prev.versione_corrente && prev.versione_corrente > 1 && (
                          <span className="px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 text-[10px] font-bold rounded">
                            V{prev.versione_corrente}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-4 px-6 text-sm font-semibold text-black dark:text-white">
                      {prev.cliente_nome || '-'}
                    </td>
                    <td className="py-4 px-6 text-sm text-gray-500 dark:text-gray-400 max-w-xs truncate">
                      {prev.oggetto || '-'}
                    </td>
                    <td className="py-4 px-6">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border capitalize ${getStatoStyle(prev.stato)}`}>
                        {prev.stato || 'bozza'}
                      </span>
                    </td>
                    <td className="py-4 px-6 text-sm text-gray-500">
                      {formatDate(prev.data_modifica)}
                    </td>
                    <td className="py-4 px-6 text-sm text-right font-medium text-black dark:text-white">
                      € {total.toLocaleString('it-IT', { minimumFractionDigits: 2 })}
                    </td>
                    <td className="py-4 px-6 text-right">
                      <div className="flex items-center justify-end gap-3 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Link
                          to={`/preventivi/${prev.id}`}
                          className="text-xs font-medium text-gray-500 hover:text-black dark:hover:text-white transition-colors"
                        >
                          Vedi
                        </Link>
                        <Link
                          to={`/preventivi/${prev.id}/edit`}
                          className="text-xs font-medium text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
                        >
                          Modifica
                        </Link>
                        <button
                          onClick={() => handleDelete(prev.id, prev.cliente_nome)}
                          className="text-xs font-medium text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 transition-colors"
                        >
                          Elimina
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {filteredPreventivi.length === 0 && (
                <tr>
                  <td colSpan="7" className="py-12 text-center text-gray-400 text-sm">
                    {searchTerm ? 'Nessun preventivo trovato per la ricerca.' : 'Nessun preventivo creato. Crea il primo!'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PreventiviList;
